// @flow
import React, { useState, useEffect } from "react";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { HStack } from "@chakra-ui/react";

import { DragHandle } from "src/components/Unifize/DragHandle";
import DragHandleIcon from "src/components/Unifize/DragHandle";
import Tooltip from "src/components/Tooltip";
import Icon from "src/icons";
import { icons } from "src/styles";

import Dropdown from "./Dropdown";
import { IconButton as SVGButton } from "src/components/Unifize/Button/styles";
import { mapFieldIntoRow, mapFieldsBySection } from "./utils";
import type { UnifizeDropdownRow } from "./utils";
import { EmbFieldOptionsContainer } from "./styles";

import * as colors from "src/styles/constants/colors";

type Props = {
  id: string,
  value: ?number,
  fields: Array<number>,
  itemIndex: number,
  isDefault: boolean,
  handleField: Function,
  addField: Function,
  removeField: Function,
  checklistFields: Array<Object>,
  handleDefaultField: Function,
  isLinkedField: boolean,
};

const FieldSelect = ({
  id,
  value,
  fields,
  handleField,
  removeField,
  addField,
  itemIndex,
  checklistFields,
  isDefault,
  handleDefaultField,
  isLinkedField,
}: Props) => {
  const { listeners, setNodeRef, setActivatorNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    width: "100%",
  };

  const [elements, setElements] = useState<Array<UnifizeDropdownRow>>([]);
  const [currentElement, setCurrentElement] =
    useState<?UnifizeDropdownRow>(null);

  const loading = value != null && currentElement === null;

  useEffect(() => {
    let sectionMap = mapFieldsBySection(checklistFields);
    // Map the fields into UnifizeDropdownRows (cells as [label,
    // icon])
    let mappedElements = sectionMap.flatMap((row) =>
      !fields.includes(row[0].id) ? mapFieldIntoRow(row) : [],
    );
    setElements(mappedElements);
    if (value) {
      let currentSection = sectionMap.find(
        (section) => section[0].id === value,
      );
      currentSection && setCurrentElement(mapFieldIntoRow(currentSection));
    }
  }, [value, fields, checklistFields]);

  const handleToggleDefaultField = () => {
    if (value) {
      handleDefaultField(value);
    }
  };

  return (
    <div ref={setNodeRef} style={style}>
      <HStack width="100%">
        <DragHandle
          dragHandleRef={setActivatorNodeRef}
          dragHandleListeners={listeners}
          icon={<DragHandleIcon />}
        />
        <Dropdown
          elements={elements}
          handleSelect={(fieldId) => handleField({ fieldId, index: itemIndex })}
          handleRemove={removeField}
          label="Select checklist field"
          selectedElement={currentElement || null}
          onChange={({ selectedItem }) => {
            if (
              selectedItem &&
              elements.length > 1 &&
              fields.slice(0, -1).indexOf(null) === -1
            ) {
              addField();
            }
          }}
          onMount={(props) => {
            if (!value && props?.openMenu) {
              props.openMenu();
            }
          }}
          loading={loading}
        />
        <EmbFieldOptionsContainer>
          {!loading && (
            <>
              {isLinkedField && (
                <Tooltip title="Visible by default" placement="top">
                  <Icon
                    style={{ cursor: value ? "pointer" : "not-allowed" }}
                    onClick={handleToggleDefaultField}
                    color={isDefault ? colors.active : colors.grey42}
                    type="eyeOutline"
                  />
                </Tooltip>
              )}
              <SVGButton onClick={() => removeField(value)}>
                <img src={icons.trash} alt="delete icon" />
              </SVGButton>
            </>
          )}
        </EmbFieldOptionsContainer>
      </HStack>
    </div>
  );
};

export default FieldSelect;
